// api.ts
import { AxiosError } from "axios";
import { patchCustomer } from "src/shared/services/servicesv2";
import { CustomerFormData } from "src/shared/services/interfacev2";
import { notifyError, notifySuccess, notifyWarning } from "src/assets/toastService";

interface ApiErrorResponse {
  message?: string;
}

export const handleUpdateError = (error: AxiosError<ApiErrorResponse>) => {
  const errorMessage = error.response?.data?.message || "Error updating customer. Please try again.";

  if (errorMessage.includes("Email already exists")) {
    notifyError("Email already exists");
  } else if (errorMessage.includes("Phone Number already exists")) {
    notifyError("Phone Number already exists");
  } else {
    notifyError(errorMessage);
  }
};

const buildPatchPayload = (original: any, updated: CustomerFormData) => {
  const payload: any = {};

  // Name fields
  if (updated.firstName !== original.firstName) payload.firstName = updated.firstName;
  if (updated.middleName !== original.middleName) payload.middleName = updated.middleName;
  if (updated.lastName !== original.lastName) payload.lastName = updated.lastName;

  // Phones: combine primary and alternative
  const updatedPhones = [...(updated.phone ? [updated.phone] : []), ...(updated.alternativePhone ? [updated.alternativePhone] : [])];

  const originalPhones = [...(original.phone ? [original.phone] : []), ...(original.alternativePhone ? [original.alternativePhone] : [])];

  const phonesChanged = updatedPhones.length !== originalPhones.length || updatedPhones.some((p, i) => p !== originalPhones[i]);

  if (phonesChanged) {
    payload.phones = updatedPhones;
  }

  // Emails
  const updatedEmails = updated.email ? [updated.email] : [];
  const originalEmails = original.email ? [original.email] : [];

  const emailsChanged = updatedEmails.length !== originalEmails.length || updatedEmails[0] !== originalEmails[0];

  if (emailsChanged) {
    payload.emails = updatedEmails;
  }

  // Addresses
  const addressChanged = updated.address !== original.address || updated.address2 !== original.address2 || updated.address3 !== original.address3 || updated.address4 !== original.address4;

  if (addressChanged) {
    payload.addresses = {
      addressLine1: updated.address,
      addressLine2: updated.address2,
      city: updated.address3,
      postalCode: updated.address4,
    };
  }

  return payload;
};

export const updateCustomerApi = async (id: string | number, formData: CustomerFormData, originalData: any, onSuccess: () => void) => {
  const payload = buildPatchPayload(originalData, formData);

  // 🛑 Nothing changed → no API call
  if (Object.keys(payload).length === 0) {
    notifyWarning("No changes to update");
    return;
  }

  try {
    const res = await patchCustomer(id, payload);

    if (res.status === 200 || res.status === 201) {
      notifySuccess("Customer updated successfully");
      onSuccess();
    }
  } catch (error) {
    // Re-throw the error to be handled by the caller
    throw error;
  }
};
